import { useQuery, useQueryClient } from "@tanstack/react-query";
import { axiosInstance, SESSION_EXPIRED_STATUS, signOutOnServer } from "@/lib/api";
import type { User } from "@/types/user.type";
import { useAuthStore } from "@/stores/authStore";
import { isAuthenticated } from "@/lib/auth";
import { useCallback, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router";
import {
  useWorkspaceStore,
  clearWorkspaceStorage,
} from "@/stores/workspaceStore";

export const useSelectedOrganization = () => {
  const { user } = useAuthStore();
  const { selectedOrganizationId } = useWorkspaceStore();
  const selectedOrganization = selectedOrganizationId
    ? (user?.userOrganizations?.find(
        (uo) => uo.organization.id === selectedOrganizationId,
      ) ?? null)
    : null;

  return {
    selectedOrganization,
  };
};

export const useCurrentUser = () => {
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { setUser, user, logout: authLogout } = useAuthStore();
  const {
    selectedOrganizationId,
    setSelectedOrganizationId,
    setSelectedBrandId,
    _hasHydrated,
  } = useWorkspaceStore();
  const { selectedOrganization } = useSelectedOrganization();
  const promptForPayment =
    searchParams.get("promptForPayment") === "true" && isAuthenticated();

  const query = useQuery({
    queryKey: ["currentUser", user?.id],
    queryFn: async () => {
      const res = await axiosInstance.get<{
        user: User;
      }>("/auth/user-info");
      return res.data.user;
    },
    enabled: isAuthenticated() && !user,
    staleTime: 5 * 60 * 1000,
    // Inherits the global retry policy: 4xx (incl. 440) never retries, an
    // unreachable backend retries up to the cap and then trips the outage page.
  });

  useEffect(() => {
    if (!query.data || !query.isSuccess || !isAuthenticated()) return;

    if (!user) {
      setUser(query.data);
    }
  }, [query.data, query.isSuccess, setUser, user]);

  const resolvedUser = user || query.data;

  useEffect(() => {
    // Wait for localStorage rehydration so we don't overwrite the last-selected org with orgs[0].
    if (!_hasHydrated || !resolvedUser || !isAuthenticated()) return;

    const orgs = resolvedUser.userOrganizations;
    // A freshly-logged-in user can briefly lack userOrganizations (the login
    // response is thinner than /me). Defer org selection until the full user
    // arrives - deciding on a partial user black-screened RootLayout.
    if (!orgs) return;

    const belongsToUser = orgs.some(
      (uo) => uo.organization.id === selectedOrganizationId,
    );
    if (!belongsToUser) {
      setSelectedOrganizationId(orgs[0]?.organization.id ?? null);
    }
  }, [
    _hasHydrated,
    resolvedUser,
    selectedOrganizationId,
    setSelectedOrganizationId,
  ]);

  const logout = useCallback(() => {
    // Withdraw this device's refresh token first, while it is still in the store.
    signOutOnServer();
    // Nothing of the last person stays on the device (journey 8).
    queryClient.clear();
    authLogout();
    setSelectedOrganizationId(null);
    setSelectedBrandId(null);
    clearWorkspaceStorage();
    navigate("/login");
  }, [
    authLogout,
    setSelectedOrganizationId,
    setSelectedBrandId,
    navigate,
    queryClient,
  ]);

  useEffect(() => {
    // Session end is owned by HTTP 440 only (axios interceptor + this backup).
    if (!query.isError) return;
    const status = (query.error as { response?: { status?: number } } | null)
      ?.response?.status;
    if (status === SESSION_EXPIRED_STATUS) {
      logout();
    }
  }, [logout, query.isError, query.error]);

  return {
    user: user || query.data,
    isLoading: query.isLoading,
    isError: query.isError,
    refetch: query.refetch,
    selectedOrganization,
    promptForPayment,
    logout,
  };
};
